import {
  Box,
  Drawer,
  DrawerOverlay,
  DrawerProps,
  Icon,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react'
import { Maximize, Minimize } from 'lucide-react'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

type DialogSize = 'md' | 'xl'

interface DialogContextProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  size: DialogSize
  setSize: (size: DialogSize) => void
}

const DialogContext = createContext({} as DialogContextProps)

const useDialogContext = () => {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogProvider')
  }
  return context
}

interface DialogProps {
  children: ReactNode
  startExpanded?: boolean
  handler?: {
    isOpen: boolean
    onOpen: () => void
    onClose: () => void
  }
}

interface DialogDrawer extends Omit<DrawerProps, 'isOpen' | 'onClose'> {
  startExpanded?: boolean
}

const Dialog = ({ children, handler }: DialogProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [size, setSize] = useState<DialogSize>('md')

  const controller = handler || { isOpen, onOpen, onClose }

  return (
    <DialogContext.Provider value={{ ...controller, size, setSize }}>
      {children}
    </DialogContext.Provider>
  )
}

const DialogTrigger = ({ children }: { children: ReactNode }) => {
  const { onOpen } = useDialogContext()

  return <Box onClick={onOpen}>{children}</Box>
}

const DialogDrawer = ({ startExpanded, ...props }: DialogDrawer) => {
  const { isOpen, onClose, size, setSize } = useDialogContext()

  useEffect(() => {
    if (startExpanded) setSize('xl')
  }, [startExpanded])

  return (
    <Drawer
      {...props}
      isOpen={isOpen}
      placement="right"
      blockScrollOnMount={false}
      onClose={onClose}
      size={size}
    >
      <DrawerOverlay />
      {props.children}
    </Drawer>
  )
}

const DialogExpandButton = () => {
  const { size, setSize } = useDialogContext()

  const isMaximized = size === 'xl'

  return (
    <IconButton
      top={2}
      right={12}
      variant="ghost"
      aria-label="Size"
      position="absolute"
      icon={<Icon as={isMaximized ? Minimize : Maximize} />}
      onClick={() => setSize(size === 'xl' ? 'md' : 'xl')}
      size="sm"
    />
  )
}

export {
  Dialog,
  DialogDrawer,
  DialogExpandButton,
  DialogTrigger,
  useDialogContext,
}
